import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  ShoppingBag, 
  Plus, 
  Eye, 
  Edit, 
  Check, 
  Clock, 
  Truck, 
  Package,
  Search,
  Filter,
  AlertCircle
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../contexts/NotificationContext';
import { achatService } from '../../services/achatService';
import { supplierService } from '../../services/supplierService';
import { AchatOrder } from '../../types';

function AchatsList() {
  const { user } = useAuth();
  const { addNotification } = useNotifications();
  const [orders, setOrders] = useState<AchatOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    supplierId: ''
  });

  useEffect(() => {
    loadOrders();
  }, [filters]);

  const loadOrders = async () => {
    try {
      setLoading(true);
      const data = await achatService.getAchatOrders(filters);
      setOrders(data);
    } catch (error) {
      addNotification({
        userId: user?.id || '',
        title: 'Erreur de chargement',
        message: 'Impossible de charger les commandes d\'achat',
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleValidateOrder = async (orderId: string) => {
    if (!user) return;
    
    try {
      await achatService.validateOrder(orderId, user.id);
      addNotification({
        userId: user.id,
        title: 'Commande validée',
        message: 'La commande a été validée et envoyée au fournisseur',
        type: 'success'
      });
      loadOrders();
    } catch (error) {
      addNotification({
        userId: user?.id || '',
        title: 'Erreur',
        message: 'Impossible de valider la commande',
        type: 'error'
      });
    }
  };

  const handleReceiveOrder = async (orderId: string, partial: boolean = false) => {
    if (!user) return;
    
    try {
      await achatService.receiveOrder(orderId, user.id, partial);
      addNotification({
        userId: user.id,
        title: partial ? 'Réception partielle' : 'Réception complète',
        message: `La commande a été ${partial ? 'partiellement' : 'complètement'} réceptionnée`,
        type: 'success'
      });
      loadOrders();
    } catch (error) {
      addNotification({
        userId: user?.id || '',
        title: 'Erreur',
        message: 'Impossible de réceptionner la commande',
        type: 'error'
      });
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'en_attente_validation':
        return <Clock className="h-4 w-4 text-orange-500" />;
      case 'validee':
        return <Check className="h-4 w-4 text-green-500" />;
      case 'en_attente_livraison':
        return <Truck className="h-4 w-4 text-blue-500" />;
      case 'livree_partiellement':
        return <Package className="h-4 w-4 text-yellow-500" />;
      case 'livree_totalement':
        return <Check className="h-4 w-4 text-green-600" />;
      default:
        return <AlertCircle className="h-4 w-4 text-gray-500" />;
    }
  };

  const canManageAchats = user?.role === 'admin' || user?.role === 'responsable_depot' || user?.role === 'responsable_bureau';
  const canValidateOrders = canManageAchats;
  const canReceiveOrders = canManageAchats || user?.role === 'responsable_equipe';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="glass-card rounded-xl card-hover">
        <div className="px-6 py-6 sm:p-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="p-2 bg-gradient-to-br from-green-500 to-green-700 rounded-lg shadow-lg">
                <ShoppingBag className="h-6 w-6 text-white" />
              </div>
              <h1 className="text-3xl font-bold text-gray-900">Achats</h1>
            </div>
            
            {canManageAchats && (
              <Link
                to="/achats/new"
                className="btn-primary px-4 py-2 text-white text-sm rounded-lg font-medium transition-all duration-200 flex items-center space-x-2"
              >
                <Plus className="h-4 w-4" />
                <span>Nouvelle commande</span>
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="glass-card rounded-xl">
        <div className="px-6 py-5 sm:px-8">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white/80"
              />
            </div>

            {/* Status Filter */}
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white/80"
            >
              <option value="">Tous les statuts</option>
              <option value="en_attente_validation">En attente validation</option>
              <option value="validee">Validée</option>
              <option value="en_attente_livraison">En attente livraison</option>
              <option value="livree_partiellement">Livrée partiellement</option>
              <option value="livree_totalement">Livrée totalement</option>
            </select>

            {/* Supplier Filter */}
            <select
              value={filters.supplierId}
              onChange={(e) => setFilters({ ...filters, supplierId: e.target.value })}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white/80"
            >
              <option value="">Tous les fournisseurs</option>
              <option value="sup-1">Bois & Matériaux SA</option>
              <option value="sup-2">Visserie Pro</option>
            </select>

            <button
              onClick={() => setFilters({ search: '', status: '', supplierId: '' })}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white/80 hover:bg-white transition-colors"
            >
              Réinitialiser
            </button>
          </div>
        </div>
      </div>

      {/* Orders List */}
      <div className="glass-card rounded-xl">
        <div className="px-6 py-5 sm:px-8 border-b border-gray-200/50">
          <h3 className="text-lg font-medium text-gray-900">
            Commandes d'achat ({orders.length})
          </h3>
        </div>

        {orders.length === 0 ? (
          <div className="text-center py-12">
            <ShoppingBag className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">Aucune commande</h3>
            <p className="mt-1 text-sm text-gray-500">
              Les commandes d'achat apparaîtront ici
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50/50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Commande
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Fournisseur
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Statut
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Montant
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white/50 divide-y divide-gray-200">
                {orders.map((order) => (
                  <tr key={order.id} className="hover:bg-gray-50/50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className={`p-2 rounded-lg mr-3 ${
                          achatService.getStatusColor(order.status).replace('text-', 'text-').replace('bg-', 'bg-')
                        }`}>
                          {getStatusIcon(order.status)}
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            #{order.orderNumber}
                          </div>
                          <div className="text-sm text-gray-500">
                            {order.items.length} article{order.items.length > 1 ? 's' : ''}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {supplierService.getSupplierName(order.supplierId)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        achatService.getStatusColor(order.status)
                      }`}>
                        {achatService.getStatusLabel(order.status)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {order.totalAmount.toFixed(2)} €
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(order.createdAt).toLocaleDateString('fr-FR')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <Link
                          to={`/achats/${order.id}`}
                          className="text-blue-600 hover:text-blue-900 p-1 rounded"
                          title="Voir les détails"
                        >
                          <Eye className="h-4 w-4" />
                        </Link>
                        
                        {canValidateOrders && order.status === 'en_attente_validation' && (
                          <button
                            onClick={() => handleValidateOrder(order.id)}
                            className="text-green-600 hover:text-green-900 p-1 rounded"
                            title="Valider la commande"
                          >
                            <Check className="h-4 w-4" />
                          </button>
                        )}
                        
                        {canReceiveOrders && order.status === 'en_attente_livraison' && (
                          <>
                            <button
                              onClick={() => handleReceiveOrder(order.id, true)}
                              className="text-yellow-600 hover:text-yellow-900 p-1 rounded"
                              title="Réception partielle"
                            >
                              <Package className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleReceiveOrder(order.id, false)}
                              className="text-green-600 hover:text-green-900 p-1 rounded"
                              title="Réception complète"
                            >
                              <Truck className="h-4 w-4" />
                            </button>
                          </>
                        )}
                        
                        {canReceiveOrders && order.status === 'livree_partiellement' && (
                          <button
                            onClick={() => handleReceiveOrder(order.id, false)}
                            className="text-green-600 hover:text-green-900 p-1 rounded"
                            title="Finaliser réception"
                          >
                            <Check className="h-4 w-4" />
                          </button>
                        )}
                        
                        {canManageAchats && order.status === 'en_attente_validation' && (
                          <Link
                            to={`/achats/${order.id}/edit`}
                            className="text-gray-600 hover:text-gray-900 p-1 rounded"
                            title="Modifier"
                          >
                            <Edit className="h-4 w-4" />
                          </Link>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default AchatsList;